import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useStaticQuery, graphql } from 'gatsby';
import { formatPrice } from '../../utils';

import SingleSelect from '../SingleSelect';

import renderIcon from '../../utils/render-icon';
import RenderStaticHTML from '../RenderStaticHTML';

const ns = `product-subscriptions`;

const ProductSubscription = ({
	price,
	priceRangeV2,
	quantity,
	sellingPlanGroup,
	updateSelectedSellingPlanId,
}) => {
	const {
		selling_plans: sellingPlans,
		include: {
			product: {
				discount_amount: discountAmount,
			},
		},
	} = sellingPlanGroup;
	const sortedSellingPlans = sellingPlans.sort(
		(a, b) => a.order_interval_frequency - b.order_interval_frequency
	);

	const subscriptionFrequencyRef = useRef();
	const subscriptionRadios = useRef([]);

	/* SETUP GOOGLE EXPERIMENT CALLBACKS */
	const [variantCompactSubscriptionBox, setVariantCompactSubscriptionBox] = useState(true);

	const {
		wp: {
			acfOptionsPdp: {
				pdp: {
					subscribeDescription,
				},
			},
		},
	} = useStaticQuery(
		graphql`
			query {
				wp {					
					acfOptionsPdp {
						pdp {
							subscribeDescription
						}
					}
				}
			}
		`
	);

	useEffect(() => {
		window.enableCompactSubscriptionBox = () => { setVariantCompactSubscriptionBox(true); };
	}, []);
	/* END GOOGLE EXPERIMENT SETUP */

	// Default to option to One-Time Purchase
	const [selectedSubcriptionRadio, setSelectedSubscriptionRadio] = useState('one-time');

	// Map frequency option to a formt that Select 'options' can use
	const formattedFrequencyOptions = plans => {
		return plans.map(plan => {
			return {
				label: plan.selling_plan_name,
				value: plan.order_interval_frequency,
			};
		});
	};

	// Formats price with optional discount
	const formattedPrice = (p, d) => {
		const {
			currencyCode,
		} = priceRangeV2.minVariantPrice;

		if (d) {
			const discount = d / 100;
			const discountedPrice = (p - (p * discount));

			return formatPrice(
				currencyCode,
				discountedPrice
			);
		}

		return formatPrice(
			currencyCode,
			p
		);
	};

	const constructSellingPlanId = (sellingPlanId) => {
		if (!sellingPlanId) {
			return null;
		}

		return `gid://shopify/SellingPlan/${sellingPlanId}`;
	};

	// Toggles subscription radio and calls updateSelectedSellingPlanId
	// with the correct selling plan id, or null if 'One-Time Purchase'
	// is selected
	const handleSubscriptionOptionChange = e => {
		const { currentTarget } = e;
		setSelectedSubscriptionRadio(currentTarget.value);

		if (currentTarget.value === 'one-time') {
			updateSelectedSellingPlanId(null);
		} else {
			updateSelectedSellingPlanId(constructSellingPlanId(sortedSellingPlans[0]?.selling_plan_id));
		}
	};

	// Sets the current value for subscription frequency and updates the
	// selected selling plan id (passed up and into add to cart component)
	const handleFrequencyChange = e => {
		sortedSellingPlans.forEach(plan => {
			if (e.value === plan.order_interval_frequency) {
				updateSelectedSellingPlanId(constructSellingPlanId(plan.selling_plan_id));
			}
		});
	};

	// on render, select One-Time Purchase by default
	useEffect(() => {
		const subscriptionRadiosArr = subscriptionRadios.current;
		if (subscriptionRadiosArr.length) {
			const oneTimeRadioEl = subscriptionRadiosArr[0];
			oneTimeRadioEl.checked = true;
		}
	}, []);

	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`${ ns }--compact`]: variantCompactSubscriptionBox,
	});

	if (!sellingPlanGroup) return null;

	return (
		<div className={rootClassnames}>
			<div className={`${ ns }__`}>
				<div className={`${ ns }__group`}>
					<div className={`${ ns }__label`}>Select Subscription</div>
					<ul>
						<li>
							<input
								ref={(el => { subscriptionRadios.current[0] = el; })}
								className={`visually-hidden`}
								type={`radio`}
								id={`one-time`}
								value={`one-time`}
								name={`select_subscription`}
								onChange={e => { handleSubscriptionOptionChange(e); }}
								checked={selectedSubcriptionRadio === 'one-time'}
							/>
							<label htmlFor={`one-time`}>
								<span className={`${ ns }__icon`}>
									{variantCompactSubscriptionBox
										? (
											<svg width={'13'} height={'9'} viewBox={'0 0 13 9'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
												<path d={'M1 4L5 8L11.5 1.5'} stroke={'white'} strokeWidth={'1.5'} strokeLinecap={'round'} strokeLinejoin={'round'} />
											</svg>
										)
										: renderIcon('checkbox')}
								</span>
								<span>
									{variantCompactSubscriptionBox
										? 'Buy Once'
										: 'One-Time Purchase' }
								</span>
								{price && (
									<span className={`${ ns }__prices`}>
										<span className={`${ ns }__price`}>
											{formattedPrice(price * quantity)}
										</span>
										{!variantCompactSubscriptionBox && (
											<span className={`${ ns }__price-unit`}>
												{`(${ formattedPrice(price) }/Item)`}
											</span>
										)}
									</span>
								)}
							</label>
						</li>
						<li>
							<input
								ref={(el => { subscriptionRadios.current[1] = el; })}
								className={`visually-hidden`}
								type={`radio`}
								id={`subscribe-save`}
								value={`subscribe-save`}
								name={`select_subscription`}
								onChange={e => { handleSubscriptionOptionChange(e); }}
								checked={selectedSubcriptionRadio === 'subscribe-save'}
							/>
							<label htmlFor={`subscribe-save`}>
								<span className={`${ ns }__icon`}>
									{variantCompactSubscriptionBox
										? (
											<svg width={'13'} height={'9'} viewBox={'0 0 13 9'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
												<path d={'M1 4L5 8L11.5 1.5'} stroke={'white'} strokeWidth={'1.5'} strokeLinecap={'round'} strokeLinejoin={'round'} />
											</svg>
										)
										: renderIcon('checkbox')}
								</span>
								<span>Auto-Ship</span>
								{price && (
									<span className={`${ ns }__prices`}>
										<span className={`${ ns }__price`}>
											{formattedPrice((price * quantity), discountAmount)}
										</span>
										{!variantCompactSubscriptionBox && (
											<span className={`${ ns }__price-unit`}>
												{`(${ formattedPrice(price, discountAmount) }/Item)`}
											</span>
										)}
									</span>
								)}
							</label>
						</li>
						<li className={`${ ns }__note`}>
							{variantCompactSubscriptionBox && (
								<span className={`${ ns }__info`}>
									<RenderStaticHTML html={subscribeDescription || 'Skip, modify, or cancel at anytime.'} />
								</span>
							)}
						</li>
					</ul>
				</div>
				{selectedSubcriptionRadio === 'subscribe-save' && (
					<div className={`${ ns }__group`}>
						<div className={`${ ns }__label`}>Select Frequency</div>
						<SingleSelect
							ref={subscriptionFrequencyRef}
							onChange={e => { handleFrequencyChange(e); }}
							options={formattedFrequencyOptions(sortedSellingPlans)}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default ProductSubscription;
